import React, { useEffect, useState } from "react";
import MainContainer from "../../layout/MainContainer";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Box,
  Button,
  FormLabel,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import IosSwitchMain from "../../components/iosSwitchMain";
import { showError, showToast } from "../../constants/toasts";
import { isValidNumber } from "../../utils/validations";
import { useLazyGetDietaryQuery } from "../../service/cms";
import { useAddExceptionalGuestMutation } from "../../service/booking";
import DeleteIcon from "@mui/icons-material/Delete";
import { USER_TYPE } from "../../utils/enums";

enum PAYER {
  PRIMARY = "Primary",
  SECONDARY = "Secondary",
}

function AddExceptionalGuest() {
  const location = useLocation();
  const { state } = location;
  const bookingId = state?.bookingId;
  const membershipType = state?.membershipType;

  const [getDietary] = useLazyGetDietaryQuery();
  const [exceptionalGuest] = useAddExceptionalGuestMutation();

  const navigate = useNavigate();
  const goBack = () => {
    navigate(-1);
  };
  const [isGuest, setisGuest] = useState(false);
  const [guest, setGuest] = useState({
    name: "",
    email: "",
    payer: "",
    guestDietaries: [] as string[], // now an array
  });
  const [dietary, setDietary] = useState([]);
  const [guestData, setGuestData] = useState<any[]>([]);
  const [numberOfGuest, setNumberOfGuest] = useState("");
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState("");
  const soloGuest = Number(numberOfGuest);
  const duoGuest = Number(numberOfGuest) * 2;
  const maxGuest = membershipType === USER_TYPE.DUO ? duoGuest : soloGuest;

  const handleDelete = (index: number) => {
    setGuestData((prev: any) =>
      prev?.filter((_: any, i: number) => i !== index)
    );
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;

    if (name === "guestDietaries") {
      setGuest((prev) => ({
        ...prev,
        [name]: typeof value === "string" ? value.split(",") : value,
      }));
    } else {
      setGuest((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const addExceptionalGuest = async () => {
    setError(true);

    if (!numberOfGuest) {
      return;
    }

    if (!amount) {
      return;
    }

    if (isGuest && !guestData?.length) {
      showError("Please add guests");
      return;
    }

    if (membershipType === USER_TYPE.DUO) {
      const primaryCount = guestData?.filter(
        (g) => g?.payer === PAYER.PRIMARY
      )?.length;
      const secondaryCount = guestData?.filter(
        (g) => g?.payer === PAYER.SECONDARY
      )?.length;

      if (primaryCount !== duoGuest / 2 || secondaryCount !== duoGuest / 2) {
        showError(
          `For Duo membership, you must assign ${duoGuest / 2} guests to Primary and ${duoGuest / 2} guests to Secondary`
        );
        return;
      }
    }

    const body = {
      bookingId: bookingId,
      numberofGuest: Number(numberOfGuest),
      guestAmount: amount,
      guestDetails: guestData || [],
    };

    try {
      const response = await exceptionalGuest(body).unwrap();
      if (response.status === 200) {
        showToast(response?.message || "");
        goBack();
      } else {
        showError(response?.message || "");
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  const handleAddGuest = () => {
    if (isGuest && (!guest.name || !guest.email || !guest.payer)) {
      showError("Please fill guest details");
      return;
    }

    const newGuest = {
      name: guest?.name,
      email: guest?.email,
      payer: guest?.payer,
      guestDietaries: guest?.guestDietaries?.map((d) => ({ name: d })),
    };

    setGuestData((prev) => [...prev, newGuest]);

    // reset form
    setGuest({
      name: "",
      email: "",
      payer: "",
      guestDietaries: [],
    });
  };

  const getDietaryApi: any = async () => {
    setLoading(true);
    try {
      const response = await getDietary({
        page: 1,
        search: "",
      }).unwrap();
      if (response?.status === 200) {
        setLoading(false);
        setDietary(response?.data || []);
      } else {
        setLoading(false);
        setDietary([]);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  useEffect(() => {
    getDietaryApi();
  }, []);

  return (
    <>
      <MainContainer>
        <div className="main_title d_flex">
          <div className="left_s">
            <h1 className="back_arrow">
              {" "}
              <IconButton onClick={() => goBack()}>
                {" "}
                <ArrowBackIcon />{" "}
              </IconButton>{" "}
              Add Exceptional User
            </h1>
          </div>
        </div>
        <div className="cards add_user_link">
          <div className="gap_p">
            <div className="control_group w_50">
              <TextField
                variant="outlined"
                placeholder="Add no. of Guests"
                fullWidth
                hiddenLabel
                // InputProps={{ readOnly: true }}
                inputProps={{ maxLength: 10 }}
                value={numberOfGuest}
                onChange={(e) => {
                  const { value } = e.target;
                  if (isValidNumber(value)) {
                    setNumberOfGuest(value);
                  }
                }}
              />
              {error && !numberOfGuest ? (
                <p className="err_msg">Number of guests is required</p>
              ) : (
                ""
              )}
            </div>

            <div className="control_group w_50">
              <TextField
                variant="outlined"
                placeholder="Amount"
                fullWidth
                hiddenLabel
                // InputProps={{ readOnly: true }}
                inputProps={{ maxLength: 10 }}
                value={amount}
                onChange={(e) => {
                  const { value } = e.target;
                  if (isValidNumber(value)) {
                    setAmount(value);
                  }
                }}
              />
              {error && !amount ? (
                <p className="err_msg">Amount is required</p>
              ) : (
                ""
              )}
            </div>
            <div className="control_group cncl_plcy w_50">
              <FormLabel style={{ color: "white" }}>
                Add Guest Details
              </FormLabel>
              <IosSwitchMain
                checked={isGuest}
                onChange={(e) => setisGuest(e.target.checked)}
                disabled={!numberOfGuest}
              />
            </div>
            {isGuest && (
              <>
                <div className="gap_p">
                  <div className="control_group w_33">
                    <TextField
                      variant="outlined"
                      placeholder="Email"
                      fullWidth
                      hiddenLabel
                      name="email"
                      value={guest.email}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="control_group w_33">
                    <TextField
                      variant="outlined"
                      placeholder="Name"
                      fullWidth
                      hiddenLabel
                      name="name"
                      value={guest.name}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form_group w_33">
                    <Select
                      multiple
                      value={guest.guestDietaries}
                      name="guestDietaries"
                      onChange={handleChange}
                      displayEmpty
                      renderValue={(selected: any) =>
                        selected?.length === 0
                          ? "Select Dietary Requirements"
                          : selected?.join(", ")
                      }
                    >
                      <MenuItem value="" disabled>
                        Select Dietary Requirements
                      </MenuItem>
                      {dietary?.map((item: any, index: number) => (
                        <MenuItem key={index} value={item}>
                          {item}
                        </MenuItem>
                      ))}
                    </Select>
                  </div>
                  <div className="control_group w_50">
                    <Select
                      className="dropse"
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      inputProps={{
                        "aria-label": "Without label",
                      }}
                      displayEmpty
                      name="payer"
                      value={guest.payer}
                      onChange={handleChange}
                    >
                      <MenuItem value="" disabled>
                        Select Payer
                      </MenuItem>
                      <MenuItem value="Primary">Primary</MenuItem>
                      <MenuItem value="Secondary">Secondary</MenuItem>
                    </Select>
                  </div>

                  {guestData?.length < maxGuest ? (
                    <div
                      className="control_group w_50"
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                      }}
                    >
                      <Button
                        className="btn btn_primary"
                        onClick={handleAddGuest}
                      >
                        Save Guest
                      </Button>
                    </div>
                  ) : null}
                </div>

                <div className="prvs_qstn_mn">
                  <div className="d_flx1">
                    {guestData?.map((g, index) => (
                      <div
                        className="question_card"
                        style={{
                          border: "1px solid #BC8F4B",
                          borderRadius: "8px",
                          padding: 10,
                        }}
                      >
                        <Box display="flex" justifyContent="space-between">
                          <div>
                            <Typography variant="body2">
                              Name: {g?.name}
                            </Typography>
                            <Typography variant="body2">
                              Email: {g?.email}
                            </Typography>
                            <Typography variant="body2">
                              Payer: {g?.payer}
                            </Typography>
                            {g?.guestDietaries.length > 0 && (
                              <Typography variant="body2">
                                Dietary: {g?.guestDietaries[0].name}
                              </Typography>
                            )}
                          </div>

                          <IconButton
                            color="error"
                            sx={{ alignSelf: "flex-start" }}
                            onClick={() => handleDelete(index)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
            <div
              role="button"
              tabIndex={0}
              className="mt_30"
              onClick={addExceptionalGuest}
            >
              <Button className="btn btn_primary">Save</Button>
            </div>
          </div>
        </div>
      </MainContainer>
    </>
  );
}
export default AddExceptionalGuest;
